import { useState, useMemo, useCallback, useRef } from 'react';
import { Wrench, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useQuery } from '@tanstack/react-query';
import { fetchTools } from '../api/tools';

type ToolsResponse = Awaited<ReturnType<typeof fetchTools>>;
type MastraTool = ToolsResponse[string];

interface ToolListItem {
  id: string;
  name: string;
  description: string;
}

const PANEL_MIN = 350;
const PANEL_MAX_PCT = 0.6;
const PANEL_STORAGE_KEY = 'atek_tools_panel_width';

function toListItems(data: ToolsResponse | undefined): ToolListItem[] {
  if (!data) return [];
  return Object.entries(data).map(([id, tool]) => ({
    id,
    name: tool.id || id,
    description: tool.description || '',
  }));
}

function prettyJson(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function ToolsListPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['mastra-tools'],
    queryFn: fetchTools,
  });
  const [search, setSearch] = useState('');
  const [selectedTool, setSelectedTool] = useState<MastraTool | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth || (containerRef.current ? containerRef.current.offsetWidth * 0.4 : 450);

    const onMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const delta = startX - ev.clientX;
      const containerWidth = containerRef.current.offsetWidth;
      const newWidth = Math.min(Math.max(startWidth + delta, PANEL_MIN), containerWidth * PANEL_MAX_PCT);
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setPanelWidth((w) => { localStorage.setItem(PANEL_STORAGE_KEY, String(w)); return w; });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  const handleSelectTool = useCallback((tool: MastraTool) => {
    setSelectedTool(tool);
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedTool(null);
    setPanelOpen(false);
  }, []);

  const items = useMemo(() => {
    let filtered = toListItems(data);
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q),
      );
    }
    return filtered;
  }, [data, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-blue-400" />
          <h1 className="text-xl font-semibold">Tools</h1>
          {data && (
            <span className="text-xs text-muted-foreground">({Object.keys(data).length})</span>
          )}
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/50 p-4 text-sm text-red-300">
          Failed to load tools. Is Mastra running?
        </div>
      )}

      <div ref={containerRef} className="flex min-h-[calc(100vh-240px)]">
        <div className="flex-1 min-w-0 overflow-auto">
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><div className="h-4 w-28 animate-pulse rounded bg-muted" /></TableCell>
                      <TableCell><div className="h-4 w-24 animate-pulse rounded bg-muted" /></TableCell>
                      <TableCell><div className="h-4 w-48 animate-pulse rounded bg-muted" /></TableCell>
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                      {search ? 'No tools match your search.' : 'No tools found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((tool) => {
                    const toolData = data?.[tool.id];
                    return (
                      <TableRow
                        key={tool.id}
                        className={`cursor-pointer hover:bg-muted/50 ${selectedTool?.id === tool.id ? 'bg-muted/70' : ''}`}
                        onClick={() => toolData && handleSelectTool(toolData)}
                      >
                        <TableCell className="font-medium text-blue-300">{tool.name}</TableCell>
                        <TableCell className="font-mono text-muted-foreground text-sm">{tool.id}</TableCell>
                        <TableCell className="text-muted-foreground max-w-md truncate">{tool.description}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {panelOpen && selectedTool && (
          <>
            <div
              className="w-1.5 shrink-0 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors bg-border rounded-full my-2"
              onMouseDown={handleDragStart}
            />
            <div className="shrink-0 min-h-0 h-full flex flex-col border-l bg-background overflow-hidden" style={{ width: panelWidth || '40%' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                <span className="text-sm font-semibold truncate">{selectedTool.id}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClosePanel}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Tool ID</Label>
                    <p className="text-sm font-mono mt-1">{selectedTool.id}</p>
                  </div>
                  {selectedTool.description && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Description</Label>
                      <p className="text-sm mt-1 whitespace-pre-wrap">{selectedTool.description}</p>
                    </div>
                  )}
                  {selectedTool.inputSchema && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Input Schema</Label>
                      <pre className="text-xs mt-1 text-muted-foreground whitespace-pre-wrap max-h-64 overflow-y-auto bg-muted/30 p-2 rounded font-mono">
                        {prettyJson(selectedTool.inputSchema)}
                      </pre>
                    </div>
                  )}
                  {selectedTool.outputSchema && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Output Schema</Label>
                      <pre className="text-xs mt-1 text-muted-foreground whitespace-pre-wrap max-h-64 overflow-y-auto bg-muted/30 p-2 rounded font-mono">
                        {prettyJson(selectedTool.outputSchema)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
